// client/src/services/weighbridgeComplianceApi.ts (TypeScript since ST-00216)
//
// The weighbridge-compliance axios client. Parameter types follow api.ts: ids
// are numbers or strings, query params and JSON bodies are not modelled per
// endpoint (the API speaks raw snake_case DB column names).
import axios from 'axios';
import { installAuthInterceptors } from './authClient';
import { installDemoMasking } from '../utils/demoMode';
import type { Id, JsonBody, QueryParams } from './api';
import type {
  ComplianceDocumentRow,
  DocumentLinkTarget,
  DocumentListParams,
  NotificationConfigSaveBody,
  PaginatedBody,
} from '../types/clientPages/weighbridgeCompliance';

// Back-compat no-op — auth is cookie-based now, nothing to initialize.
export const initializeWeighbridgeApi = () => {};

const instance = axios.create({
  baseURL:
    (import.meta.env.REACT_APP_API_URL || 'http://localhost:5002/api') + '/weighbridge-compliance',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ============================================================================
// No case-transform layer — the API speaks raw snake_case DB column names
// end-to-end. Payloads go out verbatim and responses come in verbatim.
// ============================================================================

// withCredentials + CSRF header + shared single-flight refresh on 401.
installAuthInterceptors(instance);
// Demo mode masks this client too (operator names, uploaders; PLT-12, ST-00230).
installDemoMasking(instance);

const api = {
  locations: {
    getAll: () => instance.get('/locations'),
  },
  assets: {
    getAll: (params?: QueryParams) => instance.get('/assets', { params }),
    get: (id: Id) => instance.get(`/assets/${id}`),
    create: (data: JsonBody) => instance.post('/assets', data),
    update: (id: Id, data: JsonBody) => instance.put(`/assets/${id}`, data),
    delete: (id: Id) => instance.delete(`/assets/${id}`),
    getDashboardStats: () => instance.get('/assets/dashboard/stats'),
  },
  calibrations: {
    getAll: (params?: QueryParams) => instance.get('/calibrations', { params }),
    getByAsset: (assetId: Id) => instance.get(`/calibrations/asset/${assetId}`),
    get: (id: Id) => instance.get(`/calibrations/${id}`),
    create: (data: JsonBody) => instance.post('/calibrations', data),
    update: (id: Id, data: JsonBody) => instance.put(`/calibrations/${id}`, data),
    delete: (id: Id) => instance.delete(`/calibrations/${id}`),
  },
  verifications: {
    getAll: (params?: QueryParams) => instance.get('/verifications', { params }),
    getByAsset: (assetId: Id) => instance.get(`/verifications/asset/${assetId}`),
    get: (id: Id) => instance.get(`/verifications/${id}`),
    create: (data: JsonBody) => instance.post('/verifications', data),
    update: (id: Id, data: JsonBody) => instance.put(`/verifications/${id}`, data),
    delete: (id: Id) => instance.delete(`/verifications/${id}`),
  },
  operators: {
    getAll: (params?: QueryParams) => instance.get('/operators', { params }),
    get: (id: Id) => instance.get(`/operators/${id}`),
    create: (data: JsonBody) => instance.post('/operators', data),
    update: (id: Id, data: JsonBody) => instance.put(`/operators/${id}`, data),
    delete: (id: Id) => instance.delete(`/operators/${id}`),
    getAssignments: (id: Id) => instance.get(`/operators/${id}/assignments`),
    assign: (id: Id, data: JsonBody) => instance.post(`/operators/${id}/assignments`, data),
    unassign: (id: Id, assignmentId: Id) =>
      instance.delete(`/operators/${id}/assignments/${assignmentId}`),
  },
  maintenance: {
    getAll: (params?: QueryParams) => instance.get('/maintenance', { params }),
    getByAsset: (assetId: Id) => instance.get(`/maintenance/asset/${assetId}`),
    get: (id: Id) => instance.get(`/maintenance/${id}`),
    create: (data: JsonBody) => instance.post('/maintenance', data),
    update: (id: Id, data: JsonBody) => instance.put(`/maintenance/${id}`, data),
    delete: (id: Id) => instance.delete(`/maintenance/${id}`),
  },
  documents: {
    getAll: (params?: DocumentListParams) =>
      instance.get<PaginatedBody<ComplianceDocumentRow>>('/documents', { params }),
    // ST-00008: the tenant's records of one kind that a document can be linked to.
    linkTargets: (entityType: string) =>
      instance.get<{ data: DocumentLinkTarget[] }>('/documents/link-targets', {
        params: { entityType },
      }),
    getByEntity: (entityType: string, entityId: Id) =>
      instance.get(`/documents/${entityType}/${entityId}`),
    // The instance defaults to Content-Type: application/json, and with that
    // header axios serialises a FormData body to JSON (the File becomes {}), so
    // multer saw no file and every upload failed with "No file uploaded"
    // (audit WBE-8). multipart/form-data keeps the FormData as-is; the browser
    // then sets the header itself, boundary included.
    upload: (formData: FormData) =>
      instance.post<ComplianceDocumentRow>('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    download: (id: Id) => instance.get<Blob>(`/documents/${id}/download`, { responseType: 'blob' }),
    delete: (id: Id) => instance.delete(`/documents/${id}`),
  },
  ncr: {
    getAll: (params?: QueryParams) => instance.get('/ncr', { params }),
    getStats: () => instance.get('/ncr/stats'),
    get: (id: Id) => instance.get(`/ncr/${id}`),
    create: (data: JsonBody) => instance.post('/ncr', data),
    update: (id: Id, data: JsonBody) => instance.put(`/ncr/${id}`, data),
    delete: (id: Id) => instance.delete(`/ncr/${id}`),
  },
  transactions: {
    getByAsset: (assetId: Id, params?: QueryParams) =>
      instance.get(`/assets/${assetId}/transactions`, { params }),
  },
  trends: {
    compliance: (params?: QueryParams) => instance.get('/trends/compliance', { params }),
    maintenance: (params?: QueryParams) => instance.get('/trends/maintenance', { params }),
    ncr: (params?: QueryParams) => instance.get('/trends/ncr', { params }),
  },
  notifications: {
    getConfig: (assetId: Id) => instance.get(`/notifications/config/${assetId}`),
    updateConfig: (id: Id, data: JsonBody) => instance.put(`/notifications/config/${id}`, data),
    createConfig: (data: JsonBody) => instance.post('/notifications/config', data),
    // WBE-4 (ST-00223): all four notification types of one asset in one request,
    // `{ configs: [{ notification_type, recipients, alert_days_before?, is_active }] }`.
    saveForAsset: (assetId: Id, body: NotificationConfigSaveBody) =>
      instance.put(`/notifications/config/asset/${assetId}`, body),
    getLog: (params?: QueryParams) => instance.get('/notifications/log', { params }),
  },
  audit: {
    getLog: (params?: QueryParams) => instance.get('/audit', { params }),
    getByEntity: (entityType: string, entityId: Id) =>
      instance.get(`/audit/${entityType}/${entityId}`),
  },
};

export default api;
